// Phase 26C · Price Lists admin master (SECONDARY + PRIMARY tabs).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Layers, FileSpreadsheet, Download, Upload, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const blank = (list_type) => ({ code: "", name: "", list_type, is_active: true, effective_date: new Date().toISOString().slice(0, 10) });

export default function PriceListsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("masters.manage");
  const [tab, setTab] = useState("SECONDARY");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {list, isNew}
  const [rowsEditor, setRowsEditor] = useState(null); // {list, rows, skus, dirty}
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [skus, setSkus] = useState([]);
  const [duplicating, setDuplicating] = useState(null); // {source, code, name}
  const [duplicateSaving, setDuplicateSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pl, sk] = await Promise.all([
        api.get("/price-lists", { params: { list_type: tab, page_size: 100 } }),
        api.get("/skus", { params: { page_size: 500 } }),
      ]);
      setRows(pl.data.items || []);
      setSkus((sk.data.items || sk.data || []).filter(s => s.is_active !== false));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const l = editing.list;
      if (editing.isNew) {
        await api.post("/price-lists", l);
        toast.success(`Price list '${l.code}' created`);
      } else {
        const { code, list_type, ...patch } = l;
        await api.patch(`/price-lists/${l.id}`, patch);
        toast.success(`Price list '${l.code}' updated`);
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/price-lists/${confirmDelete.id}`);
      toast.success(`Price list '${confirmDelete.code}' deleted`);
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openRowsEditor = async (list) => {
    try {
      const { data } = await api.get(`/price-lists/${list.id}/rows`);
      // Build one editor row per active SKU, prefilling if present. Keep
      // each existing row's own `id` (the price_list_row id, not sku_id) —
      // clearing the price field needs it to DELETE that row rather than
      // just being silently dropped from the upsert payload.
      const existing = new Map((data.items || []).map(r => [r.sku_id, r]));
      const editorRows = skus.map(s => {
        const ex = existing.get(s.id);
        return {
          sku_id: s.id,
          row_id: ex?.id || null,
          sku_code: s.sku_code,
          sku_name: s.name,
          price_inr: ex ? ex.price_inr : "",
          dirty: false,
        };
      });
      setRowsEditor({ list, rows: editorRows });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveRows = async () => {
    const dirty = rowsEditor.rows.filter(r => r.dirty);
    const toUpsert = dirty.filter(r => r.price_inr !== "");
    // A row that HAD a price (row_id set) and was cleared blank means
    // "remove this override" — delete it rather than upsert nothing, so it
    // actually falls back to the zone/base price instead of staying stuck
    // at its last saved value.
    const toDelete = dirty.filter(r => r.price_inr === "" && r.row_id);
    if (!toUpsert.length && !toDelete.length) { toast.info("No changes to save."); return; }
    try {
      let upserted = 0, updated = 0, errors = [];
      if (toUpsert.length) {
        const { data } = await api.put(`/price-lists/${rowsEditor.list.id}/rows`, {
          rows: toUpsert.map(r => ({ sku_id: r.sku_id, price_inr: Number(r.price_inr) }))
        });
        upserted = data.upserted; updated = data.updated; errors = data.errors || [];
      }
      let deleted = 0;
      for (const r of toDelete) {
        try {
          await api.delete(`/price-lists/${rowsEditor.list.id}/rows/${r.row_id}`);
          deleted += 1;
        } catch (e) {
          errors.push({ sku_id: r.sku_id, message: formatApiError(e) });
        }
      }
      if (errors.length) {
        toast.error(`${errors.length} row(s) failed. Check console.`);
        console.warn("Row errors:", errors);
      }
      const savedCount = upserted + updated + deleted;
      if (savedCount) toast.success(`${savedCount} row(s) saved${deleted ? ` (${deleted} cleared)` : ""}`);
      setRowsEditor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Clone a whole price list (all its rows) under a new code — the common
  // case is "same prices everywhere, except one or two SKUs for this other
  // distributor," which previously meant retyping every row by hand.
  const openDuplicate = (source) => {
    setDuplicating({
      source,
      code: `${source.code}_COPY`,
      name: `${source.name} (copy)`,
    });
  };

  const doDuplicate = async () => {
    const code = duplicating.code.trim().toUpperCase();
    if (!code) { toast.error("Code is required"); return; }
    setDuplicateSaving(true);
    try {
      const { data: newList } = await api.post("/price-lists", {
        code, name: duplicating.name.trim() || code,
        list_type: duplicating.source.list_type,
        is_active: true,
        effective_date: new Date().toISOString().slice(0, 10),
      });
      const { data: srcRows } = await api.get(`/price-lists/${duplicating.source.id}/rows`);
      const rowsToCopy = (srcRows.items || [])
        .filter(r => r.price_inr !== null && r.price_inr !== undefined)
        .map(r => ({ sku_id: r.sku_id, price_inr: r.price_inr }));
      if (rowsToCopy.length) {
        const { data: putResult } = await api.put(`/price-lists/${newList.id}/rows`, { rows: rowsToCopy });
        if (putResult.errors?.length) {
          toast.error(`${putResult.errors.length} row(s) failed to copy. Check console.`);
          console.warn("Duplicate row errors:", putResult.errors);
        }
      }
      toast.success(`'${code}' created with ${rowsToCopy.length} row(s) copied from ${duplicating.source.code}`);
      setDuplicating(null);
      await load();
      // Jump straight into the rows editor for the new list — the whole
      // point of duplicating is to tweak the one or two SKUs that differ.
      openRowsEditor(newList);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDuplicateSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-price-lists-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Price Lists" }]}
        title="Price Lists"
        subtitle="Secondary and primary price lists used by distributor groups. Every edit is saved in the history."
        favorite={{ key: "admin.price-lists", type: "screen", label: "Price Lists", route: "/admin/price-lists" }}
        testid="price-lists-page-header"
        actions={canManage ? (
          <Button data-testid="pl-new" onClick={() => setEditing({ list: blank(tab), isNew: true })}
            className="tulsi-gradient text-white h-10 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20">
            <Plus className="h-4 w-4 mr-2" /> New price list
          </Button>
        ) : null}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="SECONDARY" data-testid="pl-tab-secondary">Secondary</TabsTrigger>
          <TabsTrigger value="PRIMARY" data-testid="pl-tab-primary">Primary</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{tab === "SECONDARY" ? "Secondary" : "Primary"} price lists</CardTitle>
              <CardDescription>{tab === "SECONDARY" ? "Applied to secondary orders raised by field users on retail outlets." : "Applied to primary orders raised on distributors by the company portal."}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                : rows.length === 0 ? <div className="text-sm text-neutral-500 py-4">No price lists in this tab.</div>
                : (
                  <Table data-testid={`pl-table-${tab.toLowerCase()}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Effective</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Rows</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map(r => (
                        <TableRow key={r.id} data-testid={`pl-row-${r.code}`}>
                          <TableCell className="font-mono text-xs">{r.code}</TableCell>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell className="text-xs">{r.effective_date}</TableCell>
                          <TableCell>{r.is_active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                          <TableCell className="text-right"><Badge variant="outline">{r.row_count}</Badge></TableCell>
                          <TableCell className="text-right">
                            {canManage && (
                              <div className="flex items-center justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={() => openRowsEditor(r)}
                                  data-testid={`pl-edit-rows-${r.code}`} title="Edit rows">
                                  <Layers className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditing({ list: r, isNew: false })}
                                  data-testid={`pl-edit-${r.code}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openDuplicate(r)}
                                  data-testid={`pl-duplicate-${r.code}`} title="Duplicate this price list">
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                                {!r.code.startsWith("DEFAULT_") && (
                                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}
                                    data-testid={`pl-delete-${r.code}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit metadata dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="pl-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? "Create price list" : `Edit — ${editing?.list.code}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input value={editing.list.code} onChange={(e) => setEditing({ ...editing, list: { ...editing.list, code: e.target.value.toUpperCase() } })}
                    disabled={!editing.isNew} placeholder="STRICT_SEC" data-testid="pl-code" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Input value={editing.list.list_type} disabled />
                </div>
              </div>
              <div>
                <Label>Name</Label>
                <Input value={editing.list.name} onChange={(e) => setEditing({ ...editing, list: { ...editing.list, name: e.target.value } })}
                  data-testid="pl-name" />
              </div>
              <div>
                <Label>Effective date</Label>
                <Input type="date" value={editing.list.effective_date || ""}
                  onChange={(e) => setEditing({ ...editing, list: { ...editing.list, effective_date: e.target.value } })}
                  data-testid="pl-effective" />
              </div>
              <div className="flex items-center gap-3">
                <Switch id="pl-active-switch" checked={editing.list.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, list: { ...editing.list, is_active: v } })}
                  data-testid="pl-active" />
                <Label htmlFor="pl-active-switch">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="pl-save" className="tulsi-gradient text-white font-semibold">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing?.isNew ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate dialog — clone code/name, then copy every row */}
      <Dialog open={!!duplicating} onOpenChange={(o) => !o && setDuplicating(null)}>
        <DialogContent className="max-w-lg" data-testid="pl-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate — {duplicating?.source.code}</DialogTitle>
            <DialogDescription>
              Creates a new price list with all {duplicating?.source.row_count ?? 0} row(s) copied
              from {duplicating?.source.code}. You'll land in the rows editor right after, so you can
              change just the SKUs that differ.
            </DialogDescription>
          </DialogHeader>
          {duplicating && (
            <div className="space-y-3">
              <div>
                <Label>New code</Label>
                <Input value={duplicating.code}
                  onChange={(e) => setDuplicating({ ...duplicating, code: e.target.value.toUpperCase() })}
                  data-testid="pl-duplicate-code" />
              </div>
              <div>
                <Label>New name</Label>
                <Input value={duplicating.name}
                  onChange={(e) => setDuplicating({ ...duplicating, name: e.target.value })}
                  data-testid="pl-duplicate-name" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicating(null)} disabled={duplicateSaving}>Cancel</Button>
            <Button onClick={doDuplicate} disabled={duplicateSaving} data-testid="pl-duplicate-confirm"
              className="tulsi-gradient text-white font-semibold">
              {duplicateSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rows editor */}
      <Dialog open={!!rowsEditor} onOpenChange={(o) => !o && setRowsEditor(null)}>
        <DialogContent className="max-w-3xl" data-testid="pl-rows-dialog">
          <DialogHeader>
            <DialogTitle>Rows — {rowsEditor?.list.code}</DialogTitle>
            <DialogDescription>Set price per SKU. Blank rows are ignored. Section-C history is written per edit.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Price ₹</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowsEditor?.rows.map((r, i) => (
                  <TableRow key={r.sku_id} data-testid={`pl-editor-row-${r.sku_code}`}>
                    <TableCell className="font-mono text-xs">{r.sku_code}</TableCell>
                    <TableCell className="text-sm">{r.sku_name}</TableCell>
                    <TableCell className="text-right">
                      <Input type="number" min="0" step="0.5"
                        value={r.price_inr}
                        onChange={(e) => {
                          const next = [...rowsEditor.rows];
                          next[i] = { ...r, price_inr: e.target.value, dirty: true };
                          setRowsEditor({ ...rowsEditor, rows: next });
                        }}
                        className="h-8 w-28 ml-auto text-right"
                        data-testid={`pl-editor-price-${r.sku_code}`} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRowsEditor(null)}>Cancel</Button>
            <Button onClick={saveRows} data-testid="pl-rows-save" className="tulsi-gradient text-white font-semibold">
              Save rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete price list '{confirmDelete?.code}'?</AlertDialogTitle>
            <AlertDialogDescription>All rows are removed. Distributor groups referencing this list must be unassigned first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
